import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import {
    ElementInhoudInterface,
} from '@app/core/interfaces';
import {
    DateService,
    ErrorInterface,
    FormatCodeEnum,
    InsurancePolicyActionService,
    ProductPremiumDeterminingFactorResponseInterface,
    PurchasedProductInterface,
} from 'outshared-lib';
import { Subscription } from 'rxjs';
import { InsurancePremiumDeterminingFactorService } from '../insurance-premium-determining-factors.service';
import { mapFactorIdRequest } from '../mappers';
import { CommonModule } from '@angular/common';
import {
    WucBalloonModule,
    WucButtonModule,
    WucFormFieldLabelModule,
    WucFormFieldModule, WucFormFieldSubtextModule, WucInputAlertModule,
    WucInputDatePickerModule, WucListItemModule, WucListModule,
    WucSelectModule,
} from '@inshared/website-ui-components';

@Component({
    selector: 'ins-insurance-premium-determining-factor-form',
    templateUrl: './insurance-premium-determining-factor-form.component.html',
    styleUrls: ['./insurance-premium-determining-factor-form.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        WucFormFieldModule,
        WucSelectModule,
        WucInputDatePickerModule,
        WucFormFieldLabelModule,
        WucButtonModule,
        WucInputAlertModule,
        WucBalloonModule,
        WucFormFieldSubtextModule,
        WucListItemModule,
        WucListModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsurancePremiumDeterminingFactorFormComponent implements OnDestroy, OnInit {

    @Input() public premiumDeterminingFactor!: ProductPremiumDeterminingFactorResponseInterface;
    @Input() public purchasedProduct!: PurchasedProductInterface;

    public form: UntypedFormGroup;
    public minDate: string;
    public options: ElementInhoudInterface[];
    public errors: ErrorInterface[];
    public isDisabled: boolean;

    private subscription: Subscription;

    constructor(
        private dateService: DateService,
        private insurancePremiumDeterminingFactorService: InsurancePremiumDeterminingFactorService,
        private insurancePolicyActionService: InsurancePolicyActionService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.subscription = new Subscription();
        this.errors = [];
        this.isDisabled = false;
    }

    public get startDate(): AbstractControl {
        return this.form.get('startDate');
    }

    public get factorValue(): AbstractControl {
        return this.form.get('factorValue');
    }

    public ngOnInit(): void {
        this.createForm();
        this.setMinimumDate();
        this.getOptions(this.premiumDeterminingFactor.elementName);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public onSubmit(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        this.insurancePolicyActionService
            .addPremiumDeterminingFactor$({
                factorCode: null,
                factorId: mapFactorIdRequest(this.premiumDeterminingFactor.factorId),
                factorDate: this.getFactorDate(),
                factorIndication: this.getFactorIndication(),
                factorNumber: this.getFactorNumber(),
                startDate: this.startDate.value,
                productId: this.purchasedProduct.productId,
                productSequenceNumber: this.purchasedProduct.productSequenceNumber,
            }).subscribe({
                next: () => this.onAddPremiumDeterminingFactorSuccess(),
                error: (errors) => this.onAddPremiumDeterminingFactorError(errors),
            });

        this.isDisabled = true;
        this.errors = [];
    }

    public getOptions(elementName: string): void {
        const getOptions$ = this.insurancePremiumDeterminingFactorService.getOptions$(elementName)
            .subscribe({
                next: (options) => {
                    this.options = options;
                    this.changeDetectorRef.detectChanges();
                },
            });

        this.subscription.add(getOptions$);
    }

    private getFactorDate(): string {
        if (this.premiumDeterminingFactor.formatCode === FormatCodeEnum.Date) {
            return this.factorValue.value;
        }

        return null;
    }

    private getFactorIndication(): string {
        if (this.premiumDeterminingFactor.formatCode === FormatCodeEnum.Boolean) {
            return this.factorValue.value;
        }

        return null;
    }

    private getFactorNumber(): string {
        if (this.premiumDeterminingFactor.formatCode === FormatCodeEnum.Numeric) {
            return this.factorValue.value;
        }

        return null;
    }

    private onAddPremiumDeterminingFactorSuccess(): void {
        this.isDisabled = false;
        this.changeDetectorRef.detectChanges();
    }

    private onAddPremiumDeterminingFactorError(errors: ErrorInterface[]): void {
        this.isDisabled = false;
        this.errors = errors;
        this.changeDetectorRef.detectChanges();
    }

    private createForm(): void {
        this.form = new UntypedFormGroup({
            factorValue: new FormControl('', [Validators.required]),
            startDate: new FormControl('', [Validators.required]),
        });
    }

    private setMinimumDate(): void {
        this.minDate = this.dateService.add(1, 'day');
    }
}
